class AudioMixerService {
  constructor() {
    // WebAudio
    this.audioContext = null;
    this.masterGain = null;
    this.compressor = null;
    this.analyser = null;
    this.recordDestination = null;

    // Routing points
    this.destination = null; // where all sources connect (compressor)

    // Microphone
    this.micStream = null;
    this.micSource = null;
    this.micGain = null;

    // Media element (audio/video)
    this.currentMediaElement = null;
    this.songSource = null; // MediaElementAudioSourceNode
    this.songGain = null;

    // Remote participants
    this.participantStreams = new Map(); // id -> { source, gain }

    // Recording
    this.recorder = null;
    this.recordedChunks = [];
    this.isRecording = false;

    // Avoid multiple createMediaElementSource() per element
    this._mediaSourceMap = new WeakMap();
    this._connectedElement = null;

    // Lazy init guard
    this._graphReady = false;
    this._graphPromise = null;
  }

  async initialize() {
    // Intentionally lazy: AudioContext + graph are created on first user gesture
    // (pointerdown / keydown / play) for maximum compatibility (Safari/iOS).
    return true;
  }

  async _ensureAudioGraph() {
    if (this._graphReady) return true;
    if (this._graphPromise) return this._graphPromise;

    this._graphPromise = (async () => {
      try {
        if (this._graphReady) return true;

        const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextCtor) {
          console.error('❌ WebAudio non supporté par ce navigateur');
          return false;
        }

        this.audioContext = new AudioContextCtor();

        // Master output
        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = 1.0;
        this.masterGain.connect(this.audioContext.destination);

        // Compressor (main mix bus)
        this.compressor = this.audioContext.createDynamicsCompressor();
        this.compressor.threshold.value = -24;
        this.compressor.knee.value = 30;
        this.compressor.ratio.value = 12;
        this.compressor.attack.value = 0.003;
        this.compressor.release.value = 0.25;

        // Analyser (for visualization)
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;

        // Recording destination
        this.recordDestination = this.audioContext.createMediaStreamDestination();

        // Routing:
        // all sources -> compressor
        // compressor -> masterGain -> speakers
        // compressor -> analyser (visual only)
        // compressor -> recordDestination (recording)
        this.compressor.connect(this.masterGain);
        this.compressor.connect(this.analyser);
        this.compressor.connect(this.recordDestination);

        this.destination = this.compressor;

        this._graphReady = true;
        console.log('✅ Audio graph initialisé', { state: this.audioContext.state });
        return true;
      } catch (e) {
        console.error("❌ Erreur lors de l'initialisation audio:", e);
        return false;
      }
    })().finally(() => {
      this._graphPromise = null;
    });

    return this._graphPromise;
  }

  // Call from a user gesture (click / keydown / play)
  async ensureRunning() {
    const ok = await this._ensureAudioGraph();
    if (!ok || !this.audioContext) return false;

    try {
      if (this.audioContext.state !== 'running') {
        await this.audioContext.resume();
      }
    } catch (e) {
      // Some browsers will refuse if not triggered by a user gesture.
    }
    return this.audioContext.state === 'running';
  }

  // ---------- Media loading / playback ----------

  async loadSong(url) {
    if (!url) throw new Error('URL manquante');

    // Pause previous element
    if (this.currentMediaElement) {
      try {
        this.currentMediaElement.pause();
        this.currentMediaElement.currentTime = 0;
      } catch (e) {}
    }

    // Detach previous WebAudio connections (does NOT destroy WeakMap entry)
    this._disconnectSongChain();

    const isVideoUrl = (u = '') => {
      const clean = String(u).split('?')[0].toLowerCase();
      return (
        clean.endsWith('.mp4') ||
        clean.endsWith('.webm') ||
        clean.endsWith('.mov') ||
        clean.endsWith('.mkv') ||
        clean.endsWith('.avi') ||
        clean.endsWith('.m4v')
      );
    };

    const mediaEl = document.createElement(isVideoUrl(url) ? 'video' : 'audio');

    // Must be set BEFORE src for CORS/WebAudio
    mediaEl.crossOrigin = 'anonymous';
    mediaEl.preload = 'auto';
    mediaEl.controls = false;
    mediaEl.playsInline = true;
    mediaEl.setAttribute('playsinline', '');
    mediaEl.setAttribute('webkit-playsinline', 'true');

    // Default: allow direct playback (fallback). App may override on attach.
    mediaEl.muted = false;
    mediaEl.defaultMuted = false;
    mediaEl.volume = 1.0;

    mediaEl.src = url;

    this.currentMediaElement = mediaEl;

    // Wait for metadata so duration is available
    await new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        cleanup();
        resolve();
      };

      const cleanup = () => {
        mediaEl.removeEventListener('loadedmetadata', finish);
        mediaEl.removeEventListener('canplay', finish);
        mediaEl.removeEventListener('loadeddata', finish);
        mediaEl.removeEventListener('error', finish);
      };

      const t = setTimeout(() => {
        clearTimeout(t);
        finish();
      }, 15000);

      mediaEl.addEventListener('loadedmetadata', finish);
      mediaEl.addEventListener('canplay', finish);
      mediaEl.addEventListener('loadeddata', finish);
      mediaEl.addEventListener('error', finish);
      mediaEl.load();
    });

    console.log('✅ Média chargé', {
      tag: mediaEl.tagName,
      readyState: mediaEl.readyState,
      duration: mediaEl.duration
    });

    return mediaEl;
  }

  _disconnectSongChain() {
    if (this.songSource) {
      try { this.songSource.disconnect(); } catch (e) {}
      this.songSource = null;
    }
    if (this.songGain) {
      try { this.songGain.disconnect(); } catch (e) {}
      this.songGain = null;
    }
    this._connectedElement = null;
  }

  // Attach the media element to WebAudio mixing.
  // IMPORTANT: call this from a user gesture (Play click).
  async attachMediaElement(mediaEl) {
    if (!mediaEl) return false;

    const running = await this.ensureRunning();
    if (!running || !this.audioContext || !this.destination) {
      console.warn('⚠️ AudioContext non actif: lecture en sortie directe (fallback).');
      return false;
    }

    // Already attached
    if (this._connectedElement === mediaEl && this.songSource && this.songGain) {
      return true;
    }

    // Disconnect previous
    this._disconnectSongChain();

    // Create or reuse MediaElementSourceNode
    let sourceNode = this._mediaSourceMap.get(mediaEl);
    if (!sourceNode) {
      try {
        sourceNode = this.audioContext.createMediaElementSource(mediaEl);
        this._mediaSourceMap.set(mediaEl, sourceNode);
      } catch (e) {
        console.warn('⚠️ Impossible de créer MediaElementSourceNode (fallback direct):', e);
        return false;
      }
    }

    // Song gain
    this.songGain = this.audioContext.createGain();
    this.songGain.gain.value = 1.0;

    sourceNode.connect(this.songGain);
    this.songGain.connect(this.destination);

    this.songSource = sourceNode;
    this._connectedElement = mediaEl;

    // Note: on laisse mediaEl.muted/volume inchangés pour garantir une sortie audio,
    // certains navigateurs routent déjà l'audio vers WebAudio une fois attaché.
    console.log('✅ Média connecté au mixer', { state: this.audioContext.state });
    return true;
  }

  // ---------- Microphone ----------

  async startMicrophone() {
    const running = await this.ensureRunning();
    if (!running || !this.audioContext || !this.destination) {
      throw new Error('AudioContext bloqué. Cliquez une fois sur la page et réessayez.');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        }
      });

      this.micStream = stream;
      this.micSource = this.audioContext.createMediaStreamSource(stream);
      this.micGain = this.audioContext.createGain();
      this.micGain.gain.value = 1.0;

      this.micSource.connect(this.micGain);
      this.micGain.connect(this.destination);

      console.log('✅ Microphone démarré');
      return stream;
    } catch (error) {
      console.error("❌ Erreur lors de l'accès au microphone:", error);
      throw error;
    }
  }

  stopMicrophone() {
    if (this.micStream) {
      try { this.micStream.getTracks().forEach((t) => t.stop()); } catch (e) {}
      this.micStream = null;
    }
    if (this.micSource) {
      try { this.micSource.disconnect(); } catch (e) {}
      this.micSource = null;
    }
    if (this.micGain) {
      try { this.micGain.disconnect(); } catch (e) {}
      this.micGain = null;
    }
    console.log('🔇 Microphone arrêté');
  }

  setMicrophoneVolume(volume) {
    if (this.micGain) {
      this.micGain.gain.value = Math.max(0, Math.min(2, volume));
    }
  }

  muteMicrophone(mute) {
    if (this.micGain) {
      this.micGain.gain.value = mute ? 0 : 1.0;
    }
  }

  // ---------- Remote participants ----------

  async addParticipantStream(participantId, stream) {
    if (!participantId || !stream) return;

    const running = await this.ensureRunning();
    if (!running || !this.audioContext || !this.destination) {
      console.warn('⚠️ AudioContext non actif: participant non ajouté au mix.');
      return;
    }

    try {
      // Cleanup existing
      this.removeParticipantStream(participantId);

      const source = this.audioContext.createMediaStreamSource(stream);
      const gain = this.audioContext.createGain();
      gain.gain.value = 1.0;

      source.connect(gain);
      gain.connect(this.destination);

      this.participantStreams.set(participantId, { source, gain });
      console.log(`✅ Participant ${participantId} ajouté au mix`);
    } catch (e) {
      console.error(`❌ Erreur ajout participant ${participantId}:`, e);
    }
  }

  removeParticipantStream(participantId) {
    const data = this.participantStreams.get(participantId);
    if (!data) return;
    try { data.source.disconnect(); } catch (e) {}
    try { data.gain.disconnect(); } catch (e) {}
    this.participantStreams.delete(participantId);
    console.log(`🗑️ Participant ${participantId} retiré du mix`);
  }

  // ---------- Volumes ----------

  setSongVolume(volume) {
    if (this.songGain) {
      this.songGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  setMasterVolume(volume) {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  // ---------- Recording ----------

  async startRecording() {
    const running = await this.ensureRunning();
    if (!running || !this.recordDestination) {
      throw new Error('AudioContext bloqué (impossible de démarrer l\'enregistrement).');
    }
    if (this.isRecording) return;

    this.recordedChunks = [];
    try {
      this.recorder = new MediaRecorder(this.recordDestination.stream);
    } catch (e) {
      // Fallback: some browsers need mimeType
      this.recorder = new MediaRecorder(this.recordDestination.stream, { mimeType: 'audio/webm' });
    }

    this.recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) this.recordedChunks.push(event.data);
    };

    this.recorder.start();
    this.isRecording = true;
    console.log('⏺️ Enregistrement démarré');
  }

  async stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.recorder || !this.isRecording) {
        reject(new Error('Aucun enregistrement en cours'));
        return;
      }

      this.recorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
        this.isRecording = false;
        this.recordedChunks = [];
        console.log('⏹️ Enregistrement arrêté');
        resolve(blob);
      };

      try {
        this.recorder.stop();
      } catch (e) {
        reject(e);
      }
    });
  }

  // ---------- Diagnostics ----------

  async testTone(durationMs = 400, frequency = 440) {
    const running = await this.ensureRunning();
    if (!running || !this.audioContext || !this.masterGain) return false;

    try {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      gain.gain.value = 0.08;

      osc.type = 'sine';
      osc.frequency.value = frequency;

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start();
      setTimeout(() => {
        try { osc.stop(); } catch (e) {}
        try { osc.disconnect(); } catch (e) {}
        try { gain.disconnect(); } catch (e) {}
      }, Math.max(50, durationMs));

      return true;
    } catch (e) {
      console.warn('⚠️ testTone échoué:', e);
      return false;
    }
  }

  getAnalyserData() {
    if (!this.analyser) return null;
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);
    return dataArray;
  }

  getFrequencyData() {
    if (!this.analyser) return null;
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteFrequencyData(dataArray);
    return dataArray;
  }

  cleanup() {
    this.stopMicrophone();

    this._disconnectSongChain();

    this.participantStreams.forEach((streamData) => {
      try { streamData.source.disconnect(); } catch (e) {}
      try { streamData.gain.disconnect(); } catch (e) {}
    });
    this.participantStreams.clear();

    if (this.recorder && this.isRecording) {
      try { this.recorder.stop(); } catch (e) {}
    }

    // If you want to fully release audio, you could close() the AudioContext.
    console.log('🧹 Audio mixer nettoyé');
  }
}

export default AudioMixerService;
